import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
  Box,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslate } from 'react-admin';
import { EmptyContent } from 'src/components/empty-content';
import dayjs from 'dayjs';
import {
  TapGridBid,
  fetchTapGridClosedBids,
  fetchTapGridOpenBids,
} from '../../services/tapGridService';

const DEFAULT_LIMIT = 10;
const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];

/** "grid-SOL-USD" → "SOL/USD" */
const formatPair = (pairId: string) =>
  pairId.replace(/^grid-/, '').replace(/-/g, '/');

/** Unix seconds → "HH:mm:ss" */
const formatTime = (ts: number) => dayjs(ts * 1000).format('HH:mm:ss');

/** relative time: "11d ago" */
const timeAgo = (iso: string) => {
  const diff = dayjs().diff(dayjs(iso), 'minute');
  if (diff < 60) return `${diff}m ago`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
  return `${Math.floor(diff / 1440)}d ago`;
};

/** Compute win/loss value */
const computeValue = (row: TapGridBid) => {
  const amount = Number(row.amount);
  const coeff = Number(row.coefficient);
  if (row.status === 'won') return { sign: '+', value: ((coeff - 1) * amount).toFixed(2) };
  if (row.status === 'cancelled') {
    const refund = Number(row.refundAmount ?? 0);
    return { sign: '', value: (refund - amount ).toFixed(2) };
  }
  return { sign: '-', value: amount.toFixed(2) };
};

const ActivityCell = ({ status, t }: { status: string; t: (key: string, opts?: any) => string }) => {
  if (status === 'won')
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
        <Typography variant="body2" color="success.main" fontWeight={600}>
          {t('module.userInfo.tapGrid.won', { _: 'Win' })}
        </Typography>
      </Stack>
    );
  if (status === 'cancelled')
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <RemoveCircleOutlineIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          {t('module.userInfo.tapGrid.cancelled', { _: 'Cancelled' })}
        </Typography>
      </Stack>
    );
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <CancelIcon sx={{ color: 'error.main', fontSize: 18 }} />
      <Typography variant="body2" color="error.main" fontWeight={600}>
        {t('module.userInfo.tapGrid.lost', { _: 'Loss' })}
      </Typography>
    </Stack>
  );
};

const PairCell = ({ row }: { row: TapGridBid }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(row.ref).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Typography variant="body2" fontWeight={700}>
        {formatPair(row.pairId)}
      </Typography>
      <Box
        sx={{
          px: 0.75,
          py: 0.1,
          borderRadius: 1,
          bgcolor: 'action.selected',
          fontSize: 11,
          color: 'text.secondary',
          lineHeight: '18px',
        }}
      >
        {Number(row.coefficient)}x
      </Box>
      <Tooltip title={copied ? 'Copied!' : 'Copy ref'}>
        <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25 }}>
          <ContentCopyIcon sx={{ fontSize: 13 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

const CellColumn = ({ row }: { row: TapGridBid }) => (
  <Box>
    <Typography variant="body2">
      {row.price1} → {row.price2}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {formatTime(row.time1)} → {formatTime(row.time2)}
    </Typography>
  </Box>
);

const ValueCell = ({ row }: { row: TapGridBid }) => {
  const { sign, value } = computeValue(row);
  const isWon = row.status === 'won';
  const isCancelled = row.status === 'cancelled';
  const color = isWon ? 'success.main' : isCancelled ? 'text.secondary' : 'error.main';
  return (
    <Stack direction="row" spacing={0.5} alignItems="flex-start" justifyContent="flex-end">
      <Box textAlign="right">
        <Typography variant="body2" fontWeight={600} color={color}>
          {sign}{value} {row.currency}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {timeAgo(row.createdAt)}
        </Typography>
      </Box>
    </Stack>
  );
};

const BidsTable = ({
  rows,
  loading,
  page,
  rowsPerPage,
  hasMore,
  onPageChange,
  onRowsPerPageChange,
}: {
  rows: TapGridBid[];
  loading: boolean;
  page: number;
  rowsPerPage: number;
  hasMore: boolean;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newLimit: number) => void;
}) => {
  const t = useTranslate();

  if (loading) return <Skeleton height={320} width="100%" />;

  if (rows.length === 0)
    return <EmptyContent title={t('common.noData', { _: 'No data' })} />;

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('module.userInfo.tapGrid.activity', { _: 'Activity' })}</TableCell>
              <TableCell>{t('module.userInfo.tapGrid.pair', { _: 'Pair' })}</TableCell>
              <TableCell>{t('module.userInfo.tapGrid.cell', { _: 'Cell' })}</TableCell>
              <TableCell>{t('module.userInfo.tapGrid.cost', { _: 'Cost' })}</TableCell>
              <TableCell align="right">{t('module.userInfo.tapGrid.value', { _: 'Value' })}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell><ActivityCell status={row.status} t={t} /></TableCell>
                <TableCell><PairCell row={row} /></TableCell>
                <TableCell><CellColumn row={row} /></TableCell>
                <TableCell>
                  <Typography variant="body2">{row.amount} {row.currency}</Typography>
                </TableCell>
                <TableCell align="right"><ValueCell row={row} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={hasMore ? -1 : page * rowsPerPage + rows.length}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        onPageChange={(_e, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(e) => onRowsPerPageChange(Number(e.target.value))}
      />
    </>
  );
};

export const TapGridActiveBids = ({ record }: { record: any }) => {
  const [rows, setRows] = useState<TapGridBid[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_LIMIT);

  const userId = record?.data?.id;

  useEffect(() => {
    if (!userId) return;
    let active = true;
    setLoading(true);
    fetchTapGridOpenBids(userId, rowsPerPage, page * rowsPerPage)
      .then((data) => { if (active) setRows(data ?? []); })
      .catch(() => { if (active) setRows([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [userId, page, rowsPerPage]);

  const handlePageChange = (newPage: number) => setPage(newPage);
  const handleRowsPerPageChange = (newLimit: number) => {
    setRowsPerPage(newLimit);
    setPage(0);
  };

  return (
    <BidsTable
      rows={rows}
      loading={loading}
      page={page}
      rowsPerPage={rowsPerPage}
      hasMore={rows.length === rowsPerPage}
      onPageChange={handlePageChange}
      onRowsPerPageChange={handleRowsPerPageChange}
    />
  );
};

export const TapGridClosedBids = ({ record }: { record: any }) => {
  const [rows, setRows] = useState<TapGridBid[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_LIMIT);

  const userId = record?.data?.id;

  useEffect(() => {
    if (!userId) return;
    let active = true;
    setLoading(true);
    fetchTapGridClosedBids(userId, rowsPerPage, page * rowsPerPage)
      .then((data) => { if (active) setRows(data ?? []); })
      .catch(() => { if (active) setRows([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [userId, page, rowsPerPage]);

  const handlePageChange = (newPage: number) => setPage(newPage);
  const handleRowsPerPageChange = (newLimit: number) => {
    setRowsPerPage(newLimit);
    setPage(0);
  };

  return (
    <BidsTable
      rows={rows}
      loading={loading}
      page={page}
      rowsPerPage={rowsPerPage}
      hasMore={rows.length === rowsPerPage}
      onPageChange={handlePageChange}
      onRowsPerPageChange={handleRowsPerPageChange}
    />
  );
};
